import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { CarouselOptions } from '~/components/isolated/Carousel';

export interface UseEmblaCarouselAutoplayOptions {
  /** Autoplay delay in milliseconds */
  delay?: number;
  /** Stop autoplay on user interaction */
  stopOnInteraction?: boolean;
  /** Enable infinite loop */
  loop?: boolean;
  /** Text direction for RTL support */
  direction?: 'ltr' | 'rtl';
}

export interface UseEmblaCarouselAutoplayReturn {
  /** Ref to attach to the viewport element */
  emblaRef: ReturnType<typeof useEmblaCarousel>[0];
  /** Current selected slide index */
  selectedIndex: number;
  /** Key to reset progress animation on interaction */
  animationKey: number;
  /** Navigate to a specific slide */
  scrollTo: (index: number) => void;
  /** Navigate to the previous slide */
  scrollPrev: () => void;
  /** Navigate to the next slide */
  scrollNext: () => void;
  /** Check if can scroll to previous slide */
  canScrollPrev: boolean;
  /** Check if can scroll to next slide */
  canScrollNext: boolean;
}

export const useEmblaCarouselAutoplay = (
  slidesCount: number,
  options: UseEmblaCarouselAutoplayOptions = {},
): UseEmblaCarouselAutoplayReturn => {
  const {
    delay = 5000,
    stopOnInteraction = false,
    loop = true,
    direction = 'ltr',
  } = options;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [animationKey, setAnimationKey] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const autoplayPlugin = useMemo(
    () => Autoplay({ delay, stopOnInteraction }),
    [delay, stopOnInteraction],
  );

  const emblaOptions = useMemo<CarouselOptions>(
    () => ({
      loop: loop && slidesCount > 1,
      direction,
    }),
    [loop, slidesCount, direction],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions, [autoplayPlugin]);

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  const resetAnimation = useCallback(() => {
    setAnimationKey((prev) => prev + 1);
  }, []);

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) {
        return;
      }
      emblaApi.scrollTo(index);
    },
    [emblaApi],
  );

  const scrollPrev = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (!emblaApi) {
      return;
    }
    emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) {
      return;
    }

    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    emblaApi.on('pointerDown', resetAnimation);
    emblaApi.on('pointerUp', resetAnimation);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
      emblaApi.off('pointerDown', resetAnimation);
      emblaApi.off('pointerUp', resetAnimation);
    };
  }, [emblaApi, onSelect, resetAnimation]);

  return {
    emblaRef,
    selectedIndex,
    animationKey,
    scrollTo,
    scrollPrev,
    scrollNext,
    canScrollPrev,
    canScrollNext,
  };
};
